import { useCallback, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCommunitySuccesses, type CommunitySuccessPost } from "../services/CommunitySuccessService";
import { useUserContext } from "../context/UserContext";

const SEEN_AT_KEY = "dealhawk-community-wins-seen-at";
const LATEST_PAGE_SIZE = 20;

export const COMMUNITY_WINS_LATEST_QUERY_KEY = ["community-successes", "latest"];

const readSeenAt = (): number => {
  try {
    const value = localStorage.getItem(SEEN_AT_KEY);
    return value ? Number(value) || 0 : 0;
  } catch {
    return 0;
  }
};

// One shared "seen at" value for the whole page, so the nav bar, the bottom-nav More menu and
// the feed all react the moment any of them marks the feed as seen. Each hook instance used to
// read storage once on mount, which left the nav dot on after a visit.
const seenStore = {
  value: null as number | null,
  listeners: new Set<() => void>(),
};
const notifySeenListeners = () => seenStore.listeners.forEach((listener) => listener());
const subscribeSeen = (listener: () => void) => {
  seenStore.listeners.add(listener);
  return () => {
    seenStore.listeners.delete(listener);
  };
};
const getSeenSnapshot = () => {
  if (seenStore.value === null) seenStore.value = readSeenAt();
  return seenStore.value;
};

/**
 * Whether the community feed has posts this user has not opened yet.
 * "Seen" is a browser-local timestamp written when the feed is viewed, so the
 * indicator clears in the browser that looked, and the user's own posts never count.
 *
 * `isNew` compares against the pre-visit timestamp for the life of the hook instance,
 * so a page that calls `markSeen` can still label which posts were new on arrival.
 * Pass `{ watch: false }` when the caller already has the feed and only needs `markSeen`.
 */
export function useCommunityWinsUnseen(options: { watch?: boolean } = {}) {
  const watch = options.watch ?? true;
  const { user } = useUserContext();
  const enabled = watch && Boolean(user) && Boolean(user?.communityAdultConfirmed);
  const seenAt = useSyncExternalStore(subscribeSeen, getSeenSnapshot, getSeenSnapshot);
  const [baseline, setBaseline] = useState<number | null>(null);

  const latest = useQuery({
    queryKey: COMMUNITY_WINS_LATEST_QUERY_KEY,
    queryFn: () => getCommunitySuccesses(LATEST_PAGE_SIZE),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const newSince = baseline ?? seenAt;
  const isNew = useCallback(
    (post: Pick<CommunitySuccessPost, "createdAt" | "mine">) => post.createdAt > newSince && !post.mine,
    [newSince],
  );
  const posts = latest.data?.posts ?? [];
  const unseenCount = posts.filter((post) => post.createdAt > seenAt && !post.mine).length;

  const markSeen = useCallback(() => {
    const now = Date.now();
    setBaseline((previous) => previous ?? getSeenSnapshot());
    seenStore.value = now;
    notifySeenListeners();
    try {
      localStorage.setItem(SEEN_AT_KEY, String(now));
    } catch {
      // Storage unavailable; the in-memory value still clears the indicator for this session.
    }
  }, []);

  return { hasNew: unseenCount > 0, unseenCount, markSeen, isNew };
}
